import { create } from "zustand";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "./authStore";

interface Prompt {
  id: string;
  title: string;
  description: string;
  content: string;
  category_id?: string;
  is_featured: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  deleted: boolean;
  tags?: string[];
  published_at?: string | null;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

interface Tag {
  id: string;
  name: string;
  slug: string;
}

interface PromptState {
  prompts: Prompt[];
  categories: Category[];
  tags: Tag[];
  selectedPrompt: Prompt | null;
  selectedCategory: string;
  selectedTags: string[];
  searchQuery: string;
  loading: boolean;
  error: string | null;

  // Fetch functions
  fetchPrompts: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchTags: () => Promise<void>;
  fetchPromptById: (id: string) => Promise<void>;

  // CRUD operations
  createPrompt: (
    prompt: Omit<Prompt, "id" | "created_at" | "updated_at">,
  ) => Promise<string>;
  updatePrompt: (id: string, prompt: Partial<Prompt>) => Promise<void>;
  deletePrompt: (id: string) => Promise<void>;

  // Category operations
  createCategory: (category: Omit<Category, "id">) => Promise<string>;
  updateCategory: (id: string, category: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;

  // Tag operations
  createTag: (tag: Omit<Tag, "id" | "created_at">) => Promise<string>;
  updateTag: (id: string, tag: Partial<Tag>) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;

  // Filter operations
  setSelectedCategory: (categoryId: string) => void;
  setSelectedTags: (tagIds: string[]) => void;
  setSearchQuery: (query: string) => void;

  // Utility functions
  clearError: () => void;
  clearSelectedPrompt: () => void;
}

export const usePromptStore = create<PromptState>((set, get) => ({
  prompts: [],
  categories: [],
  tags: [],
  selectedPrompt: null,
  selectedCategory: "all",
  selectedTags: [],
  searchQuery: "",
  loading: false,
  error: null,

  fetchPrompts: async () => {
    try {
      set({ loading: true, error: null });

      const { selectedCategory, selectedTags, searchQuery } = get();

      // Start with base query
      let query = supabase
        .from("prompts")
        .select(
          `
          *,
          prompt_categories (*),
          prompt_tags_map (
            prompt_tags (*)
          )
        `,
        )
        .eq("deleted", false)
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .order("title", { ascending: true });

      // Apply category filter if not 'all'
      if (selectedCategory !== "all") {
        query = query.eq("category_id", selectedCategory);
      }

      // Apply search filter if provided
      if (searchQuery) {
        query = query.or(
          `title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`,
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error("Supabase query error:", error);
        throw new Error("Failed to fetch prompts from the database");
      }

      // Process the data to include tags
      const processedPrompts =
        data?.map((prompt) => {
          const tags =
            prompt.prompt_tags_map?.map(
              (tagMap: any) => tagMap.prompt_tags.id,
            ) || [];
          return {
            ...prompt,
            tags,
          };
        }) || [];

      // Sort prompts by published_at date (most recent first)
      // For prompts with the same published_at date or null dates, sort by title
      const sortedPrompts = processedPrompts.sort((a, b) => {
        // If both have published_at dates, compare them
        if (a.published_at && b.published_at) {
          const dateComparison =
            new Date(b.published_at).getTime() -
            new Date(a.published_at).getTime();
          if (dateComparison !== 0) return dateComparison;
        }
        // If only one has a published_at date, prioritize the one with a date
        else if (a.published_at) return -1;
        else if (b.published_at) return 1;

        // If dates are equal or both null, sort by title
        return a.title.localeCompare(b.title);
      });

      // Apply tag filtering in memory (since we can't do this easily in the query)
      const filteredPrompts =
        selectedTags.length > 0
          ? sortedPrompts.filter((prompt) =>
              selectedTags.every((tagId) => prompt.tags?.includes(tagId)),
            )
          : sortedPrompts;

      set({ prompts: filteredPrompts, loading: false });
    } catch (error) {
      console.error("Error fetching prompts:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to load prompts",
        loading: false,
        prompts: [], // Clear prompts on error
      });
    }
  },

  fetchCategories: async () => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from("prompt_categories")
        .select("*")
        .order("name");

      if (error) {
        console.error("Supabase query error:", error);
        throw new Error("Failed to fetch categories from the database");
      }

      set({ categories: data || [], loading: false });
    } catch (error) {
      console.error("Error fetching categories:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to load categories",
        loading: false,
        categories: [], // Clear categories on error
      });
    }
  },

  fetchTags: async () => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from("prompt_tags")
        .select("*")
        .order("name");

      if (error) {
        console.error("Supabase query error:", error);
        throw new Error("Failed to fetch tags from the database");
      }

      set({ tags: data || [], loading: false });
    } catch (error) {
      console.error("Error fetching tags:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to load tags",
        loading: false,
        tags: [], // Clear tags on error
      });
    }
  },

  fetchPromptById: async (id: string) => {
    try {
      set({ loading: true, error: null });
      set({ selectedPrompt: null }); // Clear previous prompt

      const { data, error } = await supabase
        .from("prompts")
        .select(
          `
          *,
          prompt_categories (*),
          prompt_tags_map (
            prompt_tags (*)
          )
        `,
        )
        .eq("id", id)
        .single();

      if (error) {
        console.error("Supabase query error:", error);
        throw new Error("Failed to fetch prompt from the database");
      }

      if (!data) {
        throw new Error("Prompt not found");
      }

      // Process tags
      const tags =
        data.prompt_tags_map?.map((tagMap: any) => tagMap.prompt_tags) || [];

      set({
        selectedPrompt: { ...data, tags },
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching prompt:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to load prompt",
        loading: false,
      });
    }
  },

  createPrompt: async (prompt) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to create prompts");
      }

      set({ loading: true, error: null });

      // First, insert the prompt
      const { data, error } = await supabase
        .from("prompts")
        .insert([
          {
            title: prompt.title,
            description: prompt.description,
            content: prompt.content,
            category_id: prompt.category_id,
            is_featured: prompt.is_featured,
            is_published: prompt.is_published,
            published_at: prompt.published_at,
            created_by: prompt.created_by,
            deleted: false,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("Supabase insert error:", error);
        throw new Error("Failed to create prompt in the database");
      }

      // Then, insert tag mappings if there are any
      if (prompt.tags && prompt.tags.length > 0) {
        const tagMappings = prompt.tags.map((tagId) => ({
          prompt_id: data.id,
          tag_id: tagId,
        }));

        const { error: tagError } = await supabase
          .from("prompt_tags_map")
          .insert(tagMappings);

        if (tagError) {
          console.error("Supabase tag mapping error:", tagError);
          throw new Error("Failed to create tag mappings");
        }
      }

      // Refresh prompts
      await get().fetchPrompts();

      set({ loading: false });
      return data.id;
    } catch (error) {
      console.error("Error creating prompt:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to create prompt",
        loading: false,
      });
      throw error;
    }
  },

  updatePrompt: async (id, promptUpdate) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to update prompts");
      }

      set({ loading: true, error: null });

      // Extract tags from the update
      const { tags, ...promptData } = promptUpdate;

      // Update the prompt
      const { error } = await supabase
        .from("prompts")
        .update({
          ...promptData,
          published_at: promptData.published_at || null, // Ensure published_at is explicitly set to null if not provided
        })
        .eq("id", id);

      if (error) {
        console.error("Supabase update error:", error);
        throw new Error("Failed to update prompt in the database");
      }

      // Update tags if provided
      if (tags !== undefined) {
        // Delete existing tag mappings
        const { error: deleteError } = await supabase
          .from("prompt_tags_map")
          .delete()
          .eq("prompt_id", id);

        if (deleteError) {
          console.error("Supabase tag deletion error:", deleteError);
          throw new Error("Failed to update tag mappings");
        }

        // Insert new tag mappings if there are any
        if (tags && tags.length > 0) {
          const tagMappings = tags.map((tagId) => ({
            prompt_id: id,
            tag_id: tagId,
          }));

          const { error: insertError } = await supabase
            .from("prompt_tags_map")
            .insert(tagMappings);

          if (insertError) {
            console.error("Supabase tag insertion error:", insertError);
            throw new Error("Failed to update tag mappings");
          }
        }
      }

      // Refresh prompts
      await get().fetchPrompts();

      // If this was the selected prompt, refresh it
      if (get().selectedPrompt?.id === id) {
        await get().fetchPromptById(id);
      }

      set({ loading: false });
    } catch (error) {
      console.error("Error updating prompt:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to update prompt",
        loading: false,
      });
    }
  },

  deletePrompt: async (id) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to delete prompts");
      }

      set({ loading: true, error: null });

      // Soft delete the prompt
      const { error } = await supabase
        .from("prompts")
        .update({ deleted: true })
        .eq("id", id);

      if (error) {
        console.error("Supabase delete error:", error);
        throw new Error("Failed to delete prompt from the database");
      }

      // Refresh prompts
      await get().fetchPrompts();

      // Clear selected prompt if it was deleted
      if (get().selectedPrompt?.id === id) {
        set({ selectedPrompt: null });
      }

      set({ loading: false });
    } catch (error) {
      console.error("Error deleting prompt:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to delete prompt",
        loading: false,
      });
    }
  },

  createCategory: async (category) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to create categories");
      }

      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from("prompt_categories")
        .insert([category])
        .select()
        .single();

      if (error) {
        console.error("Supabase insert error:", error);
        throw new Error("Failed to create category in the database");
      }

      // Refresh categories
      await get().fetchCategories();

      set({ loading: false });
      return data.id;
    } catch (error) {
      console.error("Error creating category:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to create category",
        loading: false,
      });
      throw error;
    }
  },

  updateCategory: async (id, category) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to update categories");
      }

      set({ loading: true, error: null });

      const { error } = await supabase
        .from("prompt_categories")
        .update(category)
        .eq("id", id);

      if (error) {
        console.error("Supabase update error:", error);
        throw new Error("Failed to update category in the database");
      }

      // Refresh categories
      await get().fetchCategories();

      set({ loading: false });
    } catch (error) {
      console.error("Error updating category:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to update category",
        loading: false,
      });
    }
  },

  deleteCategory: async (id) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to delete categories");
      }

      set({ loading: true, error: null });

      // First, update any prompts using this category to have null category_id
      const { error: updateError } = await supabase
        .from("prompts")
        .update({ category_id: null })
        .eq("category_id", id);

      if (updateError) {
        console.error("Supabase update error:", updateError);
        throw new Error("Failed to update prompts before category deletion");
      }

      // Then delete the category
      const { error } = await supabase
        .from("prompt_categories")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Supabase delete error:", error);
        throw new Error("Failed to delete category from the database");
      }

      // Refresh categories and prompts
      await get().fetchCategories();
      await get().fetchPrompts();

      set({ loading: false });
    } catch (error) {
      console.error("Error deleting category:", error);
      set({
        error:
          error instanceof Error ? error.message : "Failed to delete category",
        loading: false,
      });
    }
  },

  createTag: async (tag) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to create tags");
      }

      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from("prompt_tags")
        .insert([tag])
        .select()
        .single();

      if (error) {
        console.error("Supabase insert error:", error);
        throw new Error("Failed to create tag in the database");
      }

      // Refresh tags
      await get().fetchTags();

      set({ loading: false });
      return data.id;
    } catch (error) {
      console.error("Error creating tag:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to create tag",
        loading: false,
      });
      throw error;
    }
  },

  updateTag: async (id, tag) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to update tags");
      }

      set({ loading: true, error: null });

      const { error } = await supabase
        .from("prompt_tags")
        .update(tag)
        .eq("id", id);

      if (error) {
        console.error("Supabase update error:", error);
        throw new Error("Failed to update tag in the database");
      }

      // Refresh tags
      await get().fetchTags();

      set({ loading: false });
    } catch (error) {
      console.error("Error updating tag:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to update tag",
        loading: false,
      });
    }
  },

  deleteTag: async (id) => {
    try {
      // Get the current user from the auth store
      const user = useAuthStore.getState().user;
      if (!user) {
        throw new Error("You must be logged in to delete tags");
      }

      set({ loading: true, error: null });

      // Delete tag mappings first (cascade should handle this, but just to be safe)
      const { error: mappingError } = await supabase
        .from("prompt_tags_map")
        .delete()
        .eq("tag_id", id);

      if (mappingError) {
        console.error("Supabase mapping deletion error:", mappingError);
        throw new Error("Failed to delete tag mappings");
      }

      // Then delete the tag
      const { error } = await supabase
        .from("prompt_tags")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Supabase delete error:", error);
        throw new Error("Failed to delete tag from the database");
      }

      // Refresh tags and prompts
      await get().fetchTags();
      await get().fetchPrompts();

      set({ loading: false });
    } catch (error) {
      console.error("Error deleting tag:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to delete tag",
        loading: false,
      });
    }
  },

  setSelectedCategory: (categoryId) => {
    set({ selectedCategory: categoryId });
    get().fetchPrompts();
  },

  setSelectedTags: (tagIds) => {
    set({ selectedTags: tagIds });
    get().fetchPrompts();
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
    get().fetchPrompts();
  },

  clearError: () => set({ error: null }),

  clearSelectedPrompt: () => set({ selectedPrompt: null }),
}));
